import React, { useState, useEffect, useRef, useCallback } from "react";
import { Loader, Modal, Button, Stack, Text, Group } from "@mantine/core";
import {
  IconMail,
  IconPhone,
  IconCalendar,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconUser,
  IconLogout,
  IconEdit,
  IconTrophy,
  IconClock,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import EditPersonalInfoModal from "./EditPersonalInfoModal";
import SetSecurityPinModal from "./SetSecurityPinModal";
import PinVerificationModal from "./PinVerificationModal";
import ScalesPreview from "../shared/ScalesPreview/ScalesPreview";
import "./AccountPage.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";
import {
  getActivePin,
  isPinUnlockSessionActive,
} from "../../utils/pinUnlockSession";

function AccountPage() {
  const { user, logout, getVerifiedJWT, updateUser } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [repDetails, setRepDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [isEditPersonalInfoModalOpen, setIsEditPersonalInfoModalOpen] =
    useState(false);
  const [isSetSecurityPinModalOpen, setIsSetSecurityPinModalOpen] =
    useState(false);
  const [uploading, setUploading] = useState(false);
  const [isPinVerificationOpen, setIsPinVerificationOpen] = useState(false);
  const [payScalesData, setPayScalesData] = useState(null);
  const [showPayScales, setShowPayScales] = useState(false);
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthday: "",
    instagram: "",
    linkedin: "",
    saleStartDate: "",
    saleEndDate: "",
    recruitingSource: "",
    revenueGoal: "",
  });
  const [saving, setSaving] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  const hidePayScales = useCallback(() => {
    setShowPayScales(false);
    setPayScalesData(null);
  }, []);

  const handleLogout = async () => {
    await logout(true);
    navigate("/");
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPhoneNumber = phoneNumber => {
    if (!phoneNumber) return "N/A";
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt || !user?.repId) {
        setError("Failed to fetch account details");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/user/profile`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setRepDetails(data);
        setProfileImageUrl(data.profile_image_url || null);
        setError(null);
      } else {
        setError("Failed to fetch account details");
      }
    } catch (err) {
      handleGenericError(err, "Failed to fetch account details");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, user?.repId]);

  const handleFileSelect = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError("Please select an image file");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showError("Image file must be smaller than 5MB");
      return;
    }

    try {
      setUploading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(getApiEndpoint("/v1/user/profile-image"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.profile_image_url) {
          const newProfileImageUrl = result.data.profile_image_url;
          setProfileImageUrl(newProfileImageUrl);
          updateUser({ profileImageUrl: newProfileImageUrl });
        }
        showSuccess("Profile image updated successfully!");
      } else {
        showError("Failed to update profile image");
      }
    } catch (error) {
      handleGenericError(error, "Failed to update profile image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePersonalInfoUpdate = () => {
    fetchUserDetails();
    showSuccess("Personal information updated successfully!");
  };

  const handleSecurityPinUpdate = () => {
    fetchUserDetails();
  };

  // Inline editing functions
  const handleEditClick = () => {
    if (repDetails && user) {
      setEditFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phoneNumber: repDetails.phone_number || "",
        birthday: repDetails.birthday ? repDetails.birthday.split("T")[0] : "",
        instagram: repDetails.instagram_username || "",
        linkedin: repDetails.linkedin_profile || "",
        saleStartDate: repDetails.sale_start_date
          ? repDetails.sale_start_date.split("T")[0]
          : "",
        saleEndDate: repDetails.sale_end_date
          ? repDetails.sale_end_date.split("T")[0]
          : "",
        recruitingSource: repDetails.recruiting_source || "",
        revenueGoal: repDetails.revenue_goal
          ? repDetails.revenue_goal.toLocaleString()
          : "",
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      birthday: "",
      instagram: "",
      linkedin: "",
      saleStartDate: "",
      saleEndDate: "",
      recruitingSource: "",
      revenueGoal: "",
    });
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhoneChange = value => {
    const cleaned = value.replace(/\D/g, "");
    let formattedValue = cleaned;

    if (cleaned.length >= 6) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 3) {
      formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }

    setEditFormData(prev => ({
      ...prev,
      phoneNumber: formattedValue,
    }));
  };

  const handleRevenueGoalChange = value => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");

    // Format with commas
    const formatted = cleaned ? parseInt(cleaned).toLocaleString() : "";

    setEditFormData(prev => ({
      ...prev,
      revenueGoal: formatted,
    }));
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const cleanPhoneNumber = editFormData.phoneNumber.replace(/\D/g, "");
      const cleanRevenueGoal = editFormData.revenueGoal
        ? editFormData.revenueGoal.replace(/,/g, "")
        : "";

      const parsedRevenueGoal = cleanRevenueGoal
        ? parseInt(cleanRevenueGoal)
        : null;

      const apiData = {
        first_name: editFormData.firstName || null,
        last_name: editFormData.lastName || null,
        email: editFormData.email || null,
        phone_number: cleanPhoneNumber || null,
        birthday: editFormData.birthday || null,
        instagram_username: editFormData.instagram || null,
        linkedin_profile: editFormData.linkedin || null,
        sale_start_date: editFormData.saleStartDate || null,
        sale_end_date: editFormData.saleEndDate || null,
        recruiting_source: editFormData.recruitingSource || null,
        revenue_goal: parsedRevenueGoal,
      };

      const response = await fetch(getApiEndpoint("/v1/user/profile"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        const authUserUpdates = {};
        if (editFormData.firstName !== user.firstName) {
          authUserUpdates.firstName = editFormData.firstName;
        }
        if (editFormData.lastName !== user.lastName) {
          authUserUpdates.lastName = editFormData.lastName;
        }
        if (editFormData.email !== user.email) {
          authUserUpdates.email = editFormData.email;
        }

        if (Object.keys(authUserUpdates).length > 0) {
          updateUser(authUserUpdates);
        }

        await fetchUserDetails();
        setIsEditing(false);
        showSuccess("Profile updated successfully!");
      } else {
        const data = await response.json();
        showError(data.message || "Failed to update profile");
      }
    } catch (err) {
      handleGenericError(err, "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const fetchPayScales = async pin => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return null;
      }

      const response = await fetch(
        getApiEndpoint("/v1/scales/user/pay-scales"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ pin }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.data;
      } else {
        const error = await response.json();
        showError(error.message || "Failed to fetch pay scales");
        return null;
      }
    } catch (err) {
      handleGenericError(err, "Failed to fetch pay scales");
      return null;
    }
  };

  useEffect(() => {
    if (user?.repId) {
      fetchUserDetails();
    }
  }, [user?.repId, fetchUserDetails]);

  useEffect(() => {
    if (!showPayScales) return undefined;

    const interval = setInterval(() => {
      if (!isPinUnlockSessionActive()) {
        hidePayScales();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showPayScales, hidePayScales]);

  // Calculate revenue goal progress from actual data
  const calculateRevenueProgress = () => {
    const current = repDetails?.current_revenue || 0;
    const goal = repDetails?.revenue_goal || 0;
    const remaining = goal > 0 ? goal - current : 0;
    const percentage = goal > 0 ? Math.round((current / goal) * 100) : 0;
    return { current, goal, remaining, percentage };
  };

  const revenue = repDetails
    ? calculateRevenueProgress()
    : { current: 0, goal: 0, remaining: 0, percentage: 0 };

  if (loading) {
    return (
      <PageContent>
        <div className="loader-container">
          <Loader size="lg" />
          <p>Loading account details...</p>
        </div>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent>
        <div className="account-page">
          <div className="error-container">
            <p
              style={{
                color: "#dc2626",
                fontSize: "18px",
                marginBottom: "20px",
              }}
            >
              Error: {error}
            </p>
            <button
              onClick={fetchUserDetails}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #e5e7eb",
                background: "white",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
          <div className="logout-container">
            <button className="logout-btn" onClick={handleLogout}>
              <IconLogout size={16} />
              Logout
            </button>
          </div>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <div className="account-page">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-header-actions">
            {!isEditing ? (
              <>
                <button
                  className="profile-header-btn"
                  onClick={handleEditClick}
                >
                  <IconEdit size={16} />
                  Edit
                </button>
                {repDetails && user?.role !== "COMPANY_ADMIN" && (
                  <button
                    className="profile-header-btn"
                    onClick={() => setIsSetSecurityPinModalOpen(true)}
                  >
                    <IconUser size={16} />
                    {repDetails.has_security_pin
                      ? "Reset Security PIN"
                      : "Set Security PIN"}
                  </button>
                )}
                <button className="profile-header-btn" onClick={handleLogout}>
                  <IconLogout size={16} />
                  Logout
                </button>
              </>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <button
                  className="profile-header-btn"
                  onClick={() => setShowSaveConfirmModal(true)}
                  disabled={saving}
                  style={{
                    background: "#ff6b35",
                    color: "white",
                  }}
                >
                  {saving ? <Loader size="xs" color="white" /> : "Save"}
                </button>
                <button
                  className="profile-header-btn"
                  onClick={() => setShowCancelConfirmModal(true)}
                  disabled={saving}
                  style={{
                    background: "transparent",
                    border: "1px solid #e5e7eb",
                    color: "white",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="profile-header-content">
            <div
              className={`profile-avatar-wrapper ${uploading ? "uploading" : ""}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden-file-input"
                disabled={uploading}
              />
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="profile-avatar"
                  onClick={() => !uploading && fileInputRef.current?.click()}
                />
              ) : (
                <div
                  className="profile-avatar-placeholder"
                  onClick={() => !uploading && fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader size="sm" color="white" />
                  ) : (
                    getInitials()
                  )}
                </div>
              )}
            </div>

            <div className="profile-info">
              {isEditing ? (
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                >
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={e =>
                      handleInputChange("firstName", e.target.value)
                    }
                    disabled={saving}
                    placeholder="First Name"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  />
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={e =>
                      handleInputChange("lastName", e.target.value)
                    }
                    disabled={saving}
                    placeholder="Last Name"
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  />
                </div>
              ) : (
                <h1 style={{ color: "white" }}>
                  {user?.firstName} {user?.lastName}
                </h1>
              )}
              <p className="role" style={{ color: "white" }}>
                {repDetails?.rep_type_name || "Sales Representative"}
              </p>
            </div>
          </div>
        </div>

        {/* Unified Profile Information Section - All in One Card */}
        <div className="info-section">
          {/* Revenue Pacing Section */}
          {repDetails && user?.role !== "COMPANY_ADMIN" && (
            <div
              className="revenue-goal-section"
              style={{ padding: "16px 20px" }}
            >
              <div className="revenue-goal-header">
                <h3 style={{ fontSize: "16px" }}>
                  <IconTrophy className="trophy-icon" size={18} />
                  Revenue Pacing
                </h3>
                <span className="completion-text">
                  {revenue.percentage}% Complete
                </span>
              </div>
              <div className="revenue-goal-content">
                <div>
                  <p className="revenue-current">
                    ${revenue.current.toLocaleString()}
                  </p>
                  {isEditing ? (
                    <div style={{ marginTop: "8px" }}>
                      <input
                        type="text"
                        value={editFormData.revenueGoal}
                        onChange={e => handleRevenueGoalChange(e.target.value)}
                        disabled={saving}
                        placeholder="Enter goal amount"
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          width: "200px",
                        }}
                      />
                    </div>
                  ) : (
                    <p className="revenue-goal-text">
                      of ${revenue.goal.toLocaleString()} goal
                    </p>
                  )}
                </div>
                <div className="revenue-remaining">
                  <p className="amount">
                    ${revenue.remaining.toLocaleString()}
                  </p>
                  <p className="label">remaining</p>
                </div>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${revenue.percentage}%` }}
                ></div>
              </div>
            </div>
          )}
          {/* Contact Information & Social Media Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "48px",
              marginBottom: "40px",
            }}
          >
            {/* Contact Information Column */}
            <div>
              <h2 style={{ marginBottom: "24px" }}>
                <IconUser />
                Contact Information
              </h2>
              <div className="info-grid single-column">
                <div className="info-item">
                  <div className="info-icon email">
                    <IconMail />
                  </div>
                  <div className="info-content">
                    <p className="info-label">EMAIL</p>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={e =>
                          handleInputChange("email", e.target.value)
                        }
                        disabled={saving}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {repDetails?.email ? (
                          <a href={`mailto:${repDetails.email}`}>
                            {repDetails.email}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon phone">
                    <IconPhone />
                  </div>
                  <div className="info-content">
                    <p className="info-label">PHONE</p>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editFormData.phoneNumber}
                        onChange={e => handlePhoneChange(e.target.value)}
                        disabled={saving}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {formatPhoneNumber(repDetails?.phone_number)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon instagram">
                    <IconBrandInstagram />
                  </div>
                  <div className="info-content">
                    <p className="info-label">INSTAGRAM</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.instagram}
                        onChange={e =>
                          handleInputChange("instagram", e.target.value)
                        }
                        disabled={saving}
                        placeholder="@username or URL"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {repDetails?.instagram_username ? (
                          <a
                            href={
                              repDetails.instagram_username.startsWith("http")
                                ? repDetails.instagram_username
                                : `https://instagram.com/${repDetails.instagram_username.replace("@", "")}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {repDetails.instagram_username}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <div
                    className="info-icon"
                    style={{ background: "#e0e7ff", color: "#0077b5" }}
                  >
                    <IconBrandLinkedin />
                  </div>
                  <div className="info-content">
                    <p className="info-label">LINKEDIN</p>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.linkedin}
                        onChange={e =>
                          handleInputChange("linkedin", e.target.value)
                        }
                        disabled={saving}
                        placeholder="LinkedIn profile URL"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {repDetails?.linkedin_profile ? (
                          <a
                            href={repDetails.linkedin_profile}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {repDetails.linkedin_profile}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Details Column */}
            <div>
              <h2 style={{ marginBottom: "24px" }}>
                <IconUser />
                Personal Details
              </h2>
              <div className="info-grid single-column">
                <div className="info-item">
                  <div className="info-icon birthday">
                    <IconCalendar />
                  </div>
                  <div className="info-content">
                    <p className="info-label">BIRTHDAY</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editFormData.birthday}
                        onChange={e =>
                          handleInputChange("birthday", e.target.value)
                        }
                        disabled={saving}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {formatDate(repDetails?.birthday)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <div
                    className="info-icon"
                    style={{
                      background: repDetails?.is_manager
                        ? "#dcfce7"
                        : "#fee2e2",
                      color: repDetails?.is_manager ? "#16a34a" : "#dc2626",
                    }}
                  >
                    <IconUser />
                  </div>
                  <div className="info-content">
                    <p className="info-label">HAS MANAGER PRIVILEGES?</p>
                    <p
                      className="info-value"
                      style={{
                        color: repDetails?.is_manager ? "#16a34a" : "#dc2626",
                        fontWeight: "600",
                      }}
                    >
                      {repDetails?.is_manager ? "Yes" : "No"}
                    </p>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon manager">
                    <IconUser />
                  </div>
                  <div className="info-content">
                    <p className="info-label">MANAGER</p>
                    <p className="info-value">
                      {repDetails?.manager_name || "N/A"}
                    </p>
                  </div>
                </div>

                {repDetails?.recruited_by_name && (
                  <div className="info-item">
                    <div
                      className="info-icon"
                      style={{ background: "#dbeafe", color: "#2563eb" }}
                    >
                      <IconUser />
                    </div>
                    <div className="info-content">
                      <p className="info-label">RECRUITED BY</p>
                      <p className="info-value">
                        {repDetails.recruited_by_name}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Employment Details Section - Still within the same card */}
          {repDetails && user?.role !== "COMPANY_ADMIN" && (
            <>
              <h2 style={{ marginBottom: "24px" }}>
                <IconClock />
                Employment Details
              </h2>
              <div
                className="info-grid two-columns"
                style={{ marginBottom: "40px" }}
              >
                <div className="info-item">
                  <div className="info-icon start-date">
                    <IconCalendar />
                  </div>
                  <div className="info-content">
                    <p className="info-label">START DATE</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editFormData.saleStartDate}
                        onChange={e =>
                          handleInputChange("saleStartDate", e.target.value)
                        }
                        disabled={saving}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {formatDate(repDetails.sale_start_date)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon end-date">
                    <IconCalendar />
                  </div>
                  <div className="info-content">
                    <p className="info-label">END DATE</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editFormData.saleEndDate}
                        onChange={e =>
                          handleInputChange("saleEndDate", e.target.value)
                        }
                        disabled={saving}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      />
                    ) : (
                      <p className="info-value">
                        {formatDate(repDetails.sale_end_date)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Compensation Details Section */}
          <h2 style={{ marginBottom: "24px" }}>
            <IconCurrencyDollar />
            Compensation Details
          </h2>
          {!showPayScales ? (
            <div className="rep-details-compensation-empty">
              <p style={{ marginBottom: "1.5rem", maxWidth: "500px" }}>
                Compensation information is protected for security. Click below
                to view commission scales and pay details.
              </p>
              <button
                className="btn btn-lg btn-primary"
                onClick={async () => {
                  if (!repDetails.has_security_pin) {
                    showError(
                      "Please set up a security PIN first to view pay scales"
                    );
                    setIsSetSecurityPinModalOpen(true);
                    return;
                  }

                  if (isPinUnlockSessionActive()) {
                    const activePin = getActivePin();
                    if (activePin) {
                      const scales = await fetchPayScales(activePin);
                      if (scales) {
                        setPayScalesData(scales);
                        setShowPayScales(true);
                        showSuccess("Pay scales unlocked!");
                        return;
                      }
                    }
                  }

                  setIsPinVerificationOpen(true);
                }}
                style={{ padding: "12px 32px", fontSize: "16px" }}
              >
                <IconCurrencyDollar size={20} style={{ marginRight: "8px" }} />{" "}
                View Compensation Details
              </button>
            </div>
          ) : null}

          {/* Pay Scales Display - Integrated within same section */}
          {showPayScales && payScalesData && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <h2 style={{ margin: 0 }}>
                  <IconCurrencyDollar />
                  Pay Details
                </h2>
                <button
                  onClick={() => {
                    hidePayScales();
                  }}
                  style={{
                    background: "transparent",
                    border: "1px solid #e5e7eb",
                    color: "#6b7280",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = "#f9fafb";
                    e.target.style.borderColor = "#d1d5db";
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = "transparent";
                    e.target.style.borderColor = "#e5e7eb";
                  }}
                >
                  Hide Pay Details
                </button>
              </div>

              {/* Scales displayed side by side */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "24px",
                  marginBottom: "20px",
                }}
              >
                {payScalesData?.commission_scale_lines &&
                  payScalesData.commission_scale_lines.length > 0 && (
                    <div>
                      <ScalesPreview
                        scale_lines={payScalesData.commission_scale_lines}
                        name="Commission Scale"
                        showActions={false}
                        showFooter={false}
                      />
                    </div>
                  )}
                {payScalesData?.override_scale_lines &&
                  payScalesData.override_scale_lines.length > 0 && (
                    <div>
                      <ScalesPreview
                        scale_lines={payScalesData.override_scale_lines}
                        name="Override Scale"
                        showActions={false}
                        showFooter={false}
                      />
                    </div>
                  )}
                {payScalesData?.md_scale_lines &&
                  payScalesData.md_scale_lines.length > 0 && (
                    <div>
                      <ScalesPreview
                        scale_lines={payScalesData.md_scale_lines}
                        name="Marketing Deal Scale"
                        showActions={false}
                        showFooter={false}
                      />
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

        {/* Bottom Save/Cancel Buttons in Edit Mode */}
        {isEditing && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginTop: "40px",
              paddingTop: "24px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              onClick={() => setShowCancelConfirmModal(true)}
              disabled={saving}
              style={{
                padding: "12px 32px",
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "500",
                cursor: "pointer",
                color: "#6b7280",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => setShowSaveConfirmModal(true)}
              disabled={saving}
              style={{
                padding: "12px 32px",
                background: "#ff6b35",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "500",
                cursor: "pointer",
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {saving ? <Loader size="sm" color="white" /> : null}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}

        {/* Modals */}
        <EditPersonalInfoModal
          isOpen={isEditPersonalInfoModalOpen}
          onClose={() => setIsEditPersonalInfoModalOpen(false)}
          userProfile={repDetails}
          onUpdate={handlePersonalInfoUpdate}
          user={user}
        />

        <SetSecurityPinModal
          isOpen={isSetSecurityPinModalOpen}
          onClose={() => setIsSetSecurityPinModalOpen(false)}
          onUpdate={handleSecurityPinUpdate}
          isReset={repDetails?.has_security_pin || false}
        />

        <PinVerificationModal
          isOpen={isPinVerificationOpen}
          onClose={() => setIsPinVerificationOpen(false)}
          onSuccess={async pin => {
            const scales = await fetchPayScales(pin);
            if (scales) {
              setPayScalesData(scales);
              setShowPayScales(true);
              setIsPinVerificationOpen(false);
              showSuccess("Pay scales unlocked!");
            }
          }}
        />

        {/* Save Confirmation Modal */}
        <Modal
          opened={showSaveConfirmModal}
          onClose={() => setShowSaveConfirmModal(false)}
          title="Confirm Save"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to save these changes to your profile?
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowSaveConfirmModal(false)}
              >
                No, Go Back
              </Button>
              <Button
                style={{ background: "#ff6b35" }}
                onClick={async () => {
                  setShowSaveConfirmModal(false);
                  await handleSaveEdit();
                }}
              >
                Yes, Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Cancel Confirmation Modal */}
        <Modal
          opened={showCancelConfirmModal}
          onClose={() => setShowCancelConfirmModal(false)}
          title="Confirm Cancel"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to cancel? All unsaved changes will be lost.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirmModal(false)}
              >
                No, Keep Editing
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setShowCancelConfirmModal(false);
                  handleCancelEdit();
                }}
              >
                Yes, Discard Changes
              </Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </PageContent>
  );
}

export default AccountPage;
